import React from "react";
import { Box, Divider, Link as MuiLink, Stack, SvgIcon, Typography, useTheme } from "@mui/material";
import { Link } from "react-router";

import { FoodsIcon, getThemeLogo, iconMapping } from "@foodpilot/foods";
import { useTranslation } from "react-i18next";
import { useResponsiveBreakpoints } from "src/utils/useResponsiveBreakpoints";
import { useApplicationParameters } from "src/api/client-api/appParams/useApplicationParameters";
import { useFooterMenu, MenuTreeNode } from "src/api/client-api/menu/useFooterMenu";

type FooterProps = {
    organizationId?: number;
};

export function Footer(props: FooterProps) {
    const { organizationId } = props;
    const theme = useTheme();
    const { t } = useTranslation();
    const { applicationParameters } = useApplicationParameters();
    const { menuTree } = useFooterMenu(organizationId);
    const { isMobile } = useResponsiveBreakpoints();
    const currentTheme = applicationParameters?.theme || "foodpilot";
    const ThemeLogo = getThemeLogo(currentTheme);

    const renderFoodsIcon = (name?: string) => {
        if (!name) return null;
        const key = name || "menu";
        const alias: Record<string, string> = {
            scoresindicators: "scoresIndicators",
            company: "corporate",
            menu: "hamburgerMenu",
        };
        const resolved = alias[key] || key;
        const available = iconMapping as Record<string, unknown>;
        const safeKey = Object.prototype.hasOwnProperty.call(available, resolved) ? resolved : "default";
        return <FoodsIcon size={2} icon={safeKey as keyof typeof iconMapping} />;
    };

    const isExternalLink = (url?: string | null): boolean => {
        if (!url) return false;
        return url.startsWith("http://") || url.startsWith("https://");
    };

    const renderMenuItems = (nodes: MenuTreeNode[]): React.ReactNode[] => {
        return nodes
            .filter((node) => {
                // Include active items with URL or separators
                return node.item.isActive && (node.item.url || node.item.label === "separator");
            })
            .map((node) => {
                const { item } = node;

                // Handle separator
                if (item.label === "separator") {
                    return (
                        <Divider
                            key={item.id}
                            orientation="vertical"
                            flexItem
                            sx={{
                                height: "20px",
                                borderColor: theme.custom.grey[500],
                                alignSelf: "center",
                            }}
                        />
                    );
                }

                const isExternal = isExternalLink(item.url);
                const label = item.i18nKey ? t(item.i18nKey, item.label) : item.label;
                const icon = renderFoodsIcon(item.icon);

                if (isExternal) {
                    return (
                        <MuiLink key={item.id} href={item.url!} rel="noreferrer" target="_blank" underline="hover">
                            <Stack direction="row" alignItems="center" gap={0.5}>
                                {icon}
                                <Typography variant="body-bold">{label}</Typography>
                            </Stack>
                        </MuiLink>
                    );
                }

                return (
                    <Link key={item.id} to={item.url!} style={{ textDecoration: "none" }}>
                        <Stack direction="row" alignItems="center" gap={0.5}>
                            {icon}
                            <Typography variant="body-bold">{label}</Typography>
                        </Stack>
                    </Link>
                );
            });
    };

    return (
        <Stack
            width="100%"
            direction={isMobile ? "column" : "row"}
            justifyContent="space-between"
            alignItems="center"
            useFlexGap
            sx={{
                borderTop: `1px solid ${theme.custom.grey[500]}`,
                height: isMobile ? "auto" : "116px",
                marginTop: theme.spacing(2),
                padding: isMobile ? "1rem" : "0", // Même padding que le contenu
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    justifyContent: isMobile ? "center" : "space-between",
                    alignItems: "center",
                    width: isMobile ? "100%" : "calc(100% - 80px)",
                    flexDirection: isMobile ? "column" : "row",
                    gap: isMobile ? "1rem" : "0",
                    position: "relative",
                    left: isMobile ? "0" : "40px",
                }}
            >
                <Stack direction={isMobile ? "column" : "row"} alignItems="center" gap={isMobile ? "1rem" : "0"}>
                    <SvgIcon
                        component={ThemeLogo}
                        sx={{
                            width: "165px", // Increased by 50% from 110px
                            height: "47px", // Increased by 50% from 31px (rounded up)
                            flexShrink: "0",
                            marginRight: isMobile ? "0" : "56px",
                        }}
                        inheritViewBox
                    />
                    {menuTree.length > 0 && (
                        <Stack direction="row" justifyContent="space-between" alignItems="center" useFlexGap gap="32px">
                            {renderMenuItems(menuTree)}
                        </Stack>
                    )}
                </Stack>
                <Typography sx={{ color: "#A3A2A0" }} variant="body">
                    &#169;&nbsp;{`${t("footer.copyright", "Positive Solutions")} - ${new Date().getFullYear()}`}
                </Typography>
            </Box>
        </Stack>
    );
}
